/**
 * The browser-API layer behind the meeting recorder (Issue #13698).
 *
 * Split out of `components/notetaker/meeting-recorder.tsx` so that file stays
 * under the source-size ratchet, and because the two halves are different
 * kinds of thing: everything here is a thin, feature-detecting wrapper over a
 * browser API that behaves differently in every browser N0 (#13647) took into
 * a room, while the component is React state and copy.
 *
 * ## Everything here reports rather than throws
 *
 * An absent `MediaRecorder`, an unsupported container and an unavailable
 * `AudioContext` are all ordinary states of a
 * browser, not exceptions. Each function below answers with `undefined`,
 * `null` or a fallback, so the component can render what happened. Capture
 * continues wherever it still can: without an `AudioContext` there is no
 * silence signal, so chunking degrades to the fixed ceiling N0 validated.
 */

export interface MediaRecorderLike {
  state: string
  mimeType: string
  ondataavailable: ((event: { data: Blob }) => void) | null
  onstop: (() => void) | null
  onerror: ((event: unknown) => void) | null
  start: (timeslice?: number) => void
  stop: () => void
  /**
   * OPTIONAL, and feature-detected at every call site (#13800).
   *
   * Every browser this product targets has had `pause`/`resume` for years,
   * but they are not what makes a recorder usable — capture works without
   * them, and the meeting page's B3 pause control hides itself rather than
   * rendering a button that does nothing. Declaring them optional is also
   * what keeps the existing test doubles, which implement the five methods
   * above and no more, valid.
   */
  pause?: () => void
  resume?: () => void
}

export type MediaRecorderConstructor = (new (
  stream: MediaStream,
  options?: { mimeType?: string },
) => MediaRecorderLike) & {
  isTypeSupported?: (type: string) => boolean
}

/**
 * Containers to offer, best first. Probed rather than assumed: Safari and
 * Chrome do not agree, N0 went to a room to find that out, and the browser's
 * answer is what gets written to `Recording.mimeType`.
 */
export const CONTAINER_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
] as const

export const FALLBACK_CONTAINER = 'audio/webm'

export function getMediaRecorderConstructor():
  | MediaRecorderConstructor
  | undefined {
  const candidate = (
    globalThis as typeof globalThis & {
      MediaRecorder?: MediaRecorderConstructor
    }
  ).MediaRecorder
  return typeof candidate === 'function' ? candidate : undefined
}

export function pickContainer(Recorder: MediaRecorderConstructor): string {
  const supported = Recorder.isTypeSupported
  if (typeof supported !== 'function') return FALLBACK_CONTAINER
  for (const candidate of CONTAINER_CANDIDATES) {
    try {
      if (supported.call(Recorder, candidate)) return candidate
    } catch {
      // A browser whose probe throws is telling us it does not know this type; the next candidate is the answer, not a crash — see sub-issue R carve-out
    }
  }
  return FALLBACK_CONTAINER
}

/** `NotAllowedError: Permission denied` — the NAME is the useful half. */
export function describeError(error: unknown): string {
  if (error instanceof Error) {
    return error.name ? `${error.name}: ${error.message}` : error.message
  }
  // `MediaRecorder.onerror` hands over a DOM EVENT, not the error: the
  // `DOMException` is on its `.error`.
  if (typeof error === 'object' && error !== null && 'error' in error) {
    const inner = (error as { error: unknown }).error
    if (inner instanceof Error) return describeError(inner)
  }
  return String(error)
}

/** Bounds the two waits in `stopCapture`; see the constants they are given. */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export interface LevelMeter {
  read: () => number
  close: () => void
}

/**
 * An RMS level meter over the live stream, or `null` when this browser has no
 * `AudioContext`.
 *
 * `null` is a supported state rather than a failure: the caller then feeds the
 * boundary detector a constant above-threshold level, so chunking degrades to
 * the fixed ceiling — which is exactly what N0 validated. A browser that can
 * record but cannot analyse still produces bounded, independently decodable
 * chunks; it just stops cutting on thought boundaries.
 */
export function createLevelMeter(stream: MediaStream): LevelMeter | null {
  const Ctor = (
    globalThis as typeof globalThis & {
      AudioContext?: new () => AudioContext
      webkitAudioContext?: new () => AudioContext
    }
  ).AudioContext
  if (typeof Ctor !== 'function') return null

  try {
    const context = new Ctor()
    const source = context.createMediaStreamSource(stream)
    const analyser = context.createAnalyser()
    analyser.fftSize = 2048
    source.connect(analyser)
    const buffer = new Uint8Array(analyser.fftSize)

    return {
      read: () => {
        analyser.getByteTimeDomainData(buffer)
        let sum = 0
        for (const sample of buffer) {
          const centred = (sample - 128) / 128
          sum += centred * centred
        }
        return Math.sqrt(sum / buffer.length)
      },
      close: () => {
        try {
          source.disconnect()
          void context.close()
        } catch {
          // Closing a context the browser already tore down rejects, and this runs on the unmount path where a throw takes the tree with it — see sub-issue R carve-out
        }
      },
    }
  } catch {
    // No AudioContext, or a browser that refuses to analyse this stream: the caller falls back to ceiling-only chunking, which is what N0 validated — see sub-issue R carve-out
    return null
  }
}
